import React, { useContext, useEffect, useState } from "react";
import TopBar from "./common/home/TopBar";
import Footer from "./common/home/Footer";
import {
  Grid,
  Typography,
  TextField,
  Button
} from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { CONST_SITE_LABEL, MESSAGES, createTimeSlots } from "./common/Helper";
import "./common/Styles.css";
import MeetingScheduler from "./common/MeetingScheduler";
import { format } from "date-fns";
import DebugService from "../services/DebugService";
import { AppContext } from "../state/AppContext";
import AppointmentBookingApiService from "../services/AppointmentBookingApiService";
import { CONST_ROUTE_PATH } from "../route/RouteConstants";
import PhoneInput from "react-phone-number-input";
import { useNavigate } from "react-router-dom";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function AppointmentBooking() {
  const navigate = useNavigate();
  const [disable, setDisable] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [time, setTime] = useState("Choose Time");
  const [date, setDate] = useState("Choose Date");
  const [duration, setDuration] = useState("30 Minutes");
  const [availableTimeSlots, setAvailableTimeSlots] = useState(null)
  const [bookedTimeSlots, setBookedTimeSlots] = useState([])
  const [availableTimePeriodSlots, setAvailableTimePeriodSlots] = useState([])

  const { refresh, setRefresh } = useContext(AppContext)

  const [formValues, setFormValues] = useState({
    date: "",
    time: "",
    duration: duration,
    name: "",
    phoneNumber: "",
    email: "",
    textfield: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    textfield: "",
  });

  /**
   * Form validation handler
   * @returns 
   */
  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    const keys = [];
    for (const key in formValues) {
      keys.push(key);
      for (const value of keys) {
        if (value !== "phoneNumber") {
          if (!formValues[value]) {
            newErrors[value] = getErrorMessage(value);
            isValid = false;
          } else {
            newErrors[value] = "";
          }
        }
      }
    }

    if (formValues.time === "Choose Time" && formValues.date === "Choose Date") {
      isValid = false;
      setOpen(true);
      setTimeout(() => {
        setOpen(false)
      }, 5000);
      newErrors["time"] = "Please choose appointment date and time";
    } else {
      newErrors["time"] = "";
      setOpen(false)
    }

    if (isValid) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email)) {
        isValid = false;
        newErrors["email"] = MESSAGES.EMAIL_VALID;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  /**
   * Form reset handler
   * @param {*} e 
   */
  const handleReset = (e) => {
    setFormValues({
      name: "",
      phoneNumber: "",
      email: "",
      textfield: "",
    })

    setTime("Choose Time")
    setDate("Choose Date")
    setDuration("30 Minutes")
    setDisable(true)
  };

  /**
   * Handle form submit
   * @param {*} e Submit button click event
   */
  const handleSubmit = (e) => {
    setDisable(true);
    e.preventDefault();

    // Set appointment date value to be time value
    // This is required to set empty value of date
    if (formValues['time'] instanceof Date) {
      formValues['date'] = format(formValues['time'], 'yyyy-MM-dd')
    }

    if (validateForm()) {
      const startTime = new Date(formValues['time'])

      const breakDuration = duration.split(' ')
      const endTime = new Date(formValues['time'])
      endTime.setMinutes(endTime.getMinutes() + parseInt(breakDuration[0]))

      const requestBody = {
        "name": formValues['name'],
        "email": formValues['email'],
        "subject": formValues['textfield'],
        "startTime": startTime.getTime(),
        "endTime": endTime.getTime(),
        "appointmentDate": formValues['date'],
        "phoneNumber": formValues['phoneNumber']
      }

      DebugService.log(
        "AppointmentBooking Component: Form is valid. Submitting...",
        requestBody,
        startTime,
        endTime
      );

      AppointmentBookingApiService.submit(requestBody)
        .then(response => {
          DebugService.log('Appointment Booking component: Appointment booking response', response)

          if (response['message']) {
            handleReset();
            navigate(CONST_ROUTE_PATH.DEMO_CONFIRMED);
          }
        })
        .catch(e => {
          DebugService.error('Appointment Booking component: Error caught while creating appointment', requestBody)
        })
    } else {
      DebugService.log("AppointmentBooking Component: Form is not valid. Please fill out all required fields.", formValues);
      setDisable(false);
    }
  };

  /**
   * Handle input value
   * @param {*} e input field change event
   */
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormValues({
      ...formValues,
      [id]: value,
    });
  };

  /**
   * Retrieves error message
   * @param {*} fieldName 
   * @returns 
   */
  const getErrorMessage = (fieldName) => {
    switch (fieldName) {
      case "name":
        return MESSAGES.NAME_REQ;
      case "email":
        return MESSAGES.EMAIL_REQ;
      case "textfield":
        return MESSAGES.MESSAGE_REQ;
      default:
        return "Field is required";
    }
  };

  /**
   * Handle selected time slots
   * @param {*} startTimeEventEmit 
   */
  const handleTimeslotClicked = (startTimeEventEmit) => {
    DebugService.log("AppointmentBooking Component: Time selected", startTimeEventEmit);

    if (
      startTimeEventEmit.startTime !== "" &&
      startTimeEventEmit.startTime !== undefined
    ) {
      setTime(format(startTimeEventEmit.startTime, "h:mm a"));
      setDate(format(startTimeEventEmit.startTime, "EEEE, dd MMMM, yyyy"));

      setFormValues({
        ...formValues,
        date: format(startTimeEventEmit.startTime, 'yyyy-MM-dd')
      })

      setFormValues({
        ...formValues,
        time: startTimeEventEmit.startTime
      })
    }
  };

  const generateDefaultTimePeriods = () => {
    const periods = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const startTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0, 0, 0).getTime();
      const endTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 18, 0, 0, 0).getTime();
      
      periods.push({
        startTime,
        endTime
      });
    }
    return periods;
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Appointment Booking`

    // Fetch available time period slots
    AppointmentBookingApiService.getAvailableTimePeriod()
      .then(response => {
        let apiPeriods = [];
        if (response && response['data']) {
          apiPeriods = response['data'].filter(d => d['endTime'] > (new Date()).getTime());
        }
        
        const defaultPeriods = generateDefaultTimePeriods();
        const combinedPeriods = [...apiPeriods];
        
        defaultPeriods.forEach(dp => {
          const dpDateStr = format(new Date(dp.startTime), 'yyyy-MM-dd');
          const hasApiPeriod = apiPeriods.some(ap => format(new Date(ap.startTime), 'yyyy-MM-dd') === dpDateStr);
          if (!hasApiPeriod) {
            combinedPeriods.push(dp);
          }
        });
        
        setAvailableTimePeriodSlots(combinedPeriods);
      })
      .catch(e => {
        DebugService.error('Appointment Booking component: Error caught while fetching available time period slots', e)
        setAvailableTimePeriodSlots(generateDefaultTimePeriods());
      })

    // Fetch booked time slots
    AppointmentBookingApiService.getBookedTimeSlots()
      .then(response => {
        if (response['data']) {
          DebugService.log('AppointmentBooking Component: Booked time slots', response)
          setBookedTimeSlots(response['data'])
        } else {
          DebugService.error('Appointment Booking component: Error caught while fetching booked time slots. No data property found', response)
        }
      })
      .catch(e => {
        DebugService.error('Appointment Booking component: Error caught while fetching booked time slots', e)
      })
  }, [])

  useEffect(() => {
    if (availableTimePeriodSlots.length > 0) {

      const mappedBookedSlots = bookedTimeSlots.map((t) => {
        return {
          startTime: new Date(t['startTime']),
          endTime: new Date(t['endTime'])
        }
      })

      const mappedAvailableTimePeriod = availableTimePeriodSlots.map((t) => {
        return {
          startTime: new Date(t['startTime']),
          endTime: new Date(t['endTime'])
        }
      })

      const newAvailableTimeSlots = createTimeSlots(mappedAvailableTimePeriod, mappedBookedSlots)

      DebugService.log('AppointmentBooking Component: Mapped Available Time Slots', newAvailableTimeSlots)
      DebugService.log('AppointmentBooking Component: Mapped Booked Time Slots', mappedBookedSlots)
      DebugService.log('AppointmentBooking Component: Mapped Available Time Period', mappedAvailableTimePeriod)

      const currentTimestamp = (new Date()).getTime()
      const futureAvailableTimeSlots = newAvailableTimeSlots.filter(s => s.startTime.getTime() > currentTimestamp)

      setAvailableTimeSlots(futureAvailableTimeSlots)
      setRefresh(true)
    }
  }, [availableTimePeriodSlots, bookedTimeSlots])

  return (
    <div className="appointment-page">
      <TopBar />
      <Grid container spacing={5} className="main-content">
        <Grid item xs={12}>
          <Typography variant="h5" color={'#203B5A'} textAlign={'center'}>Book an Appointment</Typography>
          <Typography variant="subtitle2" color={'#425466'} textAlign={'center'}>Timezone: {new Date().toString().match(/([A-Z]+[\+-][0-9]+.*)/)[1]}</Typography>
        </Grid>

        <Grid item xs={12}>
          <MeetingScheduler
            handleStartTimeSelect={handleTimeslotClicked}
            availableTimeSlots={availableTimeSlots}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" color={'#fff'} className="details-heading" textAlign={'center'}>
            <span >Appointment Details</span></Typography>
        </Grid>

        <Grid item container spacing={2} xs={12}>

          {/* Appointment Details Left Part */}
          <Grid item container spacing={2} xs={12} md={4}>
            <Grid item xs={12}>
              <div className="details-row">
                <Typography variant="subtitle2" color={'#293F6D'} className="label">Date</Typography>
                <Typography variant="subtitle2" color={'#425466'}>{(date)}</Typography>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className="details-row">
                <Typography variant="subtitle2" className="label" color={'#293F6D'}>Time</Typography>
                <Typography variant="subtitle2" color={'#425466'}>{(time)}</Typography>
              </div>
              <Snackbar open={open} autoHideDuration={6000} anchorOrigin={{ vertical: 'center', horizontal: 'center' }} >
                <Alert severity="error" sx={{ width: '100%' }}>
                  {errors.time}
                </Alert>
              </Snackbar>
            </Grid>
            <Grid item xs={12}>
              <div className="details-row">
                <Typography variant="subtitle2" className="label" color={'#293F6D'}>Duration</Typography>
                <Typography variant="subtitle2" color={'#425466'}>{duration}</Typography>
              </div>
            </Grid>
          </Grid>

          {/* Appointment Details Right Part */}
          <Grid item container spacing={2} xs={12} md={8}>
            <Grid item xs={12} md={6}>
              <TextField
                id="textfield"
                label="Subject of meeting"
                fullWidth
                required
                multiline
                value={formValues.textfield}
                onChange={handleChange}
                error={Boolean(errors.textfield)}
                helperText={errors.textfield}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                error={Boolean(errors.name)}
                helperText={errors.name}
                id="name"
                label="Enter your Name"
                fullWidth
                required
                value={formValues.name}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="email"
                label="Enter Email"
                type="email"
                fullWidth
                required
                value={formValues.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PhoneInput
                className="phone-number"
                defaultCountry="IN"
                placeholder="Enter phone number"
                onChange={(value) => {
                  setFormValues({
                    ...formValues,
                    ['phoneNumber']: value,
                  })
                }}
                value={formValues.phoneNumber}
              />
            </Grid>
            <Grid item xs={12} md={12} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
              <Button
                className="submit-button"
                onClick={handleSubmit}
                type="submit"
                variant="contained"
                disabled={disable}
                style={{
                  height: '56px',
                  width: '150px'
                }}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Grid>

      </Grid>

      <Footer />
    </div>
  );
}
